<!-- Admin Role Confirmation Modal -->
<div class="modal fade" id="adminRoleModal" tabindex="-1" aria-labelledby="adminRoleModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="adminRoleModalLabel">
          <i class="bi me-2" [class.bi-shield-plus]="!userClaims?.isAdmin" [class.bi-shield-minus]="userClaims?.isAdmin"></i>
          {{ userClaims?.isAdmin ? 'Revoke Admin Access' : 'Grant Admin Access' }}
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="mb-0">
          Are you sure you want to <strong>{{ userClaims?.isAdmin ? 'revoke admin access from' : 'grant admin access to' }}</strong> this user?
        </p>
        <div *ngIf="user" class="mt-3 p-3 border rounded">
          <div class="fw-medium">{{ user.firstName }} {{ user.lastName }}</div>
          <small class="text-muted">{{ user.email }}</small>
        </div>
        <div *ngIf="!userClaims?.isAdmin" class="alert alert-info mt-3 mb-0 small">
          <i class="bi bi-info-circle me-2"></i>
          This user will be able to access the Admin Portal and manage all resources.
        </div>
        <div *ngIf="userClaims?.isAdmin" class="alert alert-warning mt-3 mb-0 small">
          <i class="bi bi-exclamation-triangle me-2"></i>
          This user will lose access to the Admin Portal immediately.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
        <button
          type="button"
          class="btn"
          [class.btn-primary]="!userClaims?.isAdmin"
          [class.btn-danger]="userClaims?.isAdmin"
          [disabled]="togglingAdmin"
          (click)="confirmToggleAdminRole()"
        >
          <span *ngIf="togglingAdmin" class="spinner-border spinner-border-sm me-2"></span>
          {{ userClaims?.isAdmin ? 'Revoke Admin' : 'Grant Admin' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Email Verification Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'verify-email'"
  title="Verify User Email"
  message="Are you sure you want to verify this user's email?"
  confirmLabel="Verify Email"
  processingLabel="Verifying..."
  variant="primary"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'unverify-email'"
  title="Unverify Email"
  message="Are you sure you want to unverify this user's email?"
  confirmLabel="Unverify Email"
  processingLabel="Updating..."
  variant="danger"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<!-- Password Reset Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'reset-password'"
  title="Send Password Reset"
  message="Are you sure you want to send a password reset email to this user?"
  confirmLabel="Send Reset Email"
  processingLabel="Sending..."
  variant="primary"
  icon="bi bi-key"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

@if (showConfirmModal && confirmModalAction === 'password-reset-flag') {
  <app-confirmation-modal
    title="Require Password Reset"
    [message]="user?.requiresPasswordReset
      ? 'This will clear the reset requirement and let them sign in normally.'
      : 'This user will not be able to sign in until they set a new password.'"
    [confirmLabel]="user?.requiresPasswordReset 
      ? 'Clear Password Reset '
      : 'Require Password Reset'"
    processingLabel="Updating..."
    variant="primary"
    icon="bi bi-shield-lock"
    [processing]="confirmModalProcessing"
    (confirmed)="onConfirmAction()"
    (cancelled)="onCancelConfirm()">
  </app-confirmation-modal>
}

<!-- Account Lock/Unlock Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'lock-account'"
  title="Lock Account"
  message="Are you sure you want to lock this user's account?"
  confirmLabel="Lock Account"
  processingLabel="Locking..."
  variant="danger"
  icon="bi bi-lock"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'unlock-account'"
  title="Unlock Account"
  message="Are you sure you want to unlock this user's account?"
  confirmLabel="Unlock Account"
  processingLabel="Unlocking..."
  variant="primary"
  icon="bi bi-unlock"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to {{ goBackText() }}
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- User Details -->
    <div *ngIf="!loading && user" class="row">
      <!-- Left Column - Profile Info -->
      <div class="col-lg-8 mb-4">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="bi bi-person-circle me-2"></i>
                User Profile
              </h5>
              <button
                *ngIf="!editMode"
                type="button"
                class="btn btn-sm btn-primary"
                (click)="toggleEditMode()"
              >
                <i class="bi bi-pencil me-2"></i>
                Edit
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- View Mode -->
            <div *ngIf="!editMode">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label text-muted small">First Name</label>
                  <div class="fw-medium">{{ user.firstName }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-muted small">Last Name</label>
                  <div class="fw-medium">{{ user.lastName }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-muted small">Email Address</label>
                  <div class="fw-medium">
                    <a [href]="'mailto:' + user.email" class="link-body-emphasis link-underline-opacity-0 link-underline-opacity-75-hover">{{ user.email }}</a>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-muted small">Phone</label>
                  <div class="fw-medium">{{ (user.phone | phoneFormat) || '—' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label text-muted small">User ID</label>
                  <div><code>{{ user.id }}</code></div>
                </div>

                <!-- Divider -->
                <div class="col-12">
                  <hr class="mt-0">
                </div>

                <!-- Address -->
                <div class="col-sm-6 mb-3">
                  <h6 class="mb-3">Address</h6>
                  <label class="form-label text-muted small">Street Address</label>
                  <div class="fw-medium mb-3">{{ user.address || '—' }}</div>
                  <div class="row">
                    <div class="col">
                      <label class="form-label text-muted small">City</label>
                      <div class="fw-medium">{{ user.city || '—' }}</div>
                    </div>
                    <div class="col">
                      <label class="form-label text-muted small">State</label>
                      <div class="fw-medium">{{ user.state || '—' }}</div>
                    </div>
                    <div class="col">
                      <label class="form-label text-muted small">ZIP</label>
                      <div class="fw-medium">{{ user.zipCode || '—' }}</div>
                    </div>
                  </div>
                </div>

                <!-- Coordinates -->
                <div class="col-sm-6 mb-3">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="mb-0">Coordinates</h6>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      (click)="lookupCoordinates()"
                      [disabled]="lookingUpCoordinates || !user.address || !user.city || !user.state">
                      <i class="bi bi-crosshair me-1"></i>
                      {{ user.latitude != null ? 'Refresh' : 'Look up' }}
                    </button>
                  </div>
                  <div class="row">
                    <div class="col">
                      <label class="form-label text-muted small">Latitude</label>
                      <div class="fw-medium">{{ user.latitude != null ? (user.latitude | number:'1.5-5') : '—' }}</div>
                    </div>
                    <div class="col">
                      <label class="form-label text-muted small">Longitude</label>
                      <div class="fw-medium">{{ user.longitude != null ? (user.longitude | number:'1.5-5') : '—' }}</div>
                    </div>
                  </div>
                  @if (user.latitude != null && user.longitude != null) {
                    <a class="small text-decoration-none d-inline-block mt-1"
                      [href]="'https://www.google.com/maps?q=' + user.latitude + ',' + user.longitude"
                      target="_blank" rel="noopener">
                      <i class="bi bi-geo-alt me-1"></i>View on map
                    </a>
                  }
                </div>

              </div>
            </div>

            <!-- Edit Mode -->
            <form *ngIf="editMode" [formGroup]="editForm" (ngSubmit)="onSubmit()">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="firstName" class="form-label">First Name</label>
                  <input
                    type="text"
                    class="form-control"
                    id="firstName"
                    formControlName="firstName"
                    [class.is-invalid]="editForm.get('firstName')?.invalid && editForm.get('firstName')?.touched"
                  />
                  <div *ngIf="editForm.get('firstName')?.invalid && editForm.get('firstName')?.touched" class="invalid-feedback">
                    First name is required
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label for="lastName" class="form-label">Last Name</label>
                  <input
                    type="text"
                    class="form-control"
                    id="lastName"
                    formControlName="lastName"
                    [class.is-invalid]="editForm.get('lastName')?.invalid && editForm.get('lastName')?.touched"
                  />
                  <div *ngIf="editForm.get('lastName')?.invalid && editForm.get('lastName')?.touched" class="invalid-feedback">
                    Last name is required
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label for="email" class="form-label">Email Address</label>
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    formControlName="email"
                    [class.is-invalid]="editForm.get('email')?.invalid && editForm.get('email')?.touched"
                  />
                  <div *ngIf="editForm.get('email')?.invalid && editForm.get('email')?.touched" class="invalid-feedback">
                    Valid email is required
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label for="phone" class="form-label">Phone</label>
                  <input
                    type="tel"
                    class="form-control"
                    id="phone"
                    formControlName="phone"
                    mask="(000) 000-0000"
                    [validation]="false"
                    placeholder="e.g. (555) 123-4567"
                  />
                </div>
              </div>

              <!-- Divider -->
              <div class="col-12">
                <hr class="mt-0">
              </div>

              <!-- Address -->
              <div class="col-12 mb-4">
                <h6 class="mb-3">Address</h6>
                <label for="address" class="form-label">Street Address</label>
                <input type="text" class="form-control mb-3" id="address" formControlName="address" />
                <div class="row">
                  <div class="col">
                    <label for="city" class="form-label">City</label>
                    <input type="text" class="form-control" id="city" formControlName="city" />
                  </div>
                  <div class="col">
                    <label for="state" class="form-label">State</label>
                    <app-us-state-select inputId="state" formControlName="state"></app-us-state-select>
                  </div>
                  <div class="col">
                    <label for="zipCode" class="form-label">ZIP</label>
                    <input
                      type="text"
                      class="form-control"
                      id="zipCode"
                      formControlName="zipCode"
                      [class.is-invalid]="editForm.get('zipCode')?.invalid && editForm.get('zipCode')?.touched"
                    />
                    <div class="invalid-feedback">Invalid ZIP</div>
                  </div>
                </div>
              </div>

              <!-- Coordinates -->
              <div class="col-12 mb-4">
                <h6 class="mb-3">Coordinates</h6>
                <div class="row">
                  <div class="col">
                    <label for="latitude" class="form-label">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      class="form-control"
                      id="latitude"
                      formControlName="latitude"
                      [class.is-invalid]="editForm.get('latitude')?.invalid && editForm.get('latitude')?.touched"
                    />
                    <div class="invalid-feedback">Must be between -90 and 90</div>
                  </div>
                  <div class="col">
                    <label for="longitude" class="form-label">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      class="form-control"
                      id="longitude"
                      formControlName="longitude"
                      [class.is-invalid]="editForm.get('longitude')?.invalid && editForm.get('longitude')?.touched"
                    />
                    <div class="invalid-feedback">Must be between -180 and 180</div>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2">
                <button type="submit" class="btn btn-primary" [disabled]="saving || editForm.invalid">
                  <span *ngIf="saving" class="spinner-border spinner-border-sm me-2"></span>
                  {{ saving ? 'Saving...' : 'Save Changes' }}
                </button>
                <button type="button" class="btn btn-outline-secondary" (click)="toggleEditMode()" [disabled]="saving">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Account Metadata -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-info-circle me-2"></i>
              Account Information
            </h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Created</label>
                <div>{{ formatDate(user.createdAt) }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Last Updated</label>
                <div>{{ formatDate(user.updatedAt) }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Membership Plan</label>
                <div>
                  @let membershipBadge = getMembershipPlanBadge(user.membershipPlan);
                  <span [class]="membershipBadge.badge">
                    <i [class]="membershipBadge.icon"></i>
                    {{ membershipBadge.label }}
                  </span>
                  <div class="text-muted small mt-1">
                    Highest tier across this user's organizations. Display only — see Organization Memberships below for per-org detail; not used for feature access.
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Last Login</label>
                <div>{{ formatDate(user.lastLogin) }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Failed Login Attempts</label>
                <div>
                  <span class="badge" [class.bg-success]="(user.failedLoginAttempts ?? 0) === 0" [class.bg-warning]="(user.failedLoginAttempts ?? 0) > 0">
                    {{ user.failedLoginAttempts }}
                  </span>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">First Login Location</label>
                <div>
                  @let firstLocation = getLoginPlatformBadge(user.firstLoginLocation);
                  <span [class]="firstLocation.badge">
                    <i [class]="firstLocation.icon"></i>
                    @if (user.firstLoginLocation) {
                      {{ user.firstLoginLocation | titlecase }}
                    } @else {
                      Unknown
                    }
                  </span>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small">Last Login Location</label>
                <div>
                  @let lastLocation = getLoginPlatformBadge(user.lastLoginLocation);
                  <span [class]="lastLocation.badge">
                    <i [class]="lastLocation.icon"></i>
                    @if (user.lastLoginLocation) {
                      {{ user.lastLoginLocation | titlecase }}
                    } @else {
                      Unknown
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Account Certifications -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-award"></i>
              Account Certifications
            </h5>
          </div>
          <app-certifications-card class="p-3"
            [userId]="userId">
          </app-certifications-card>
        </div>
      </div>

      <!-- Right Column - Status & Actions -->
      <div class="col-lg-4">
        <!-- Account Status -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-shield-check me-2"></i>
              Status
            </h5>
          </div>
          <div class="card-body">
            <div class="py-2 border-bottom">
              <label class="small text-muted d-block mb-2">Email Verification</label>
              <div class="d-flex align-items-center gap-2">
                <span class="badge" [class.bg-success]="user.emailVerified" [class.bg-warning]="!user.emailVerified">
                  <i class="bi" [class.bi-check-circle]="user.emailVerified" [class.bi-exclamation-circle]="!user.emailVerified"></i>
                  {{ user.emailVerified ? 'Verified' : 'Unverified' }}
                </span>
                <button
                  type="button"
                  class="btn btn-link btn-lg p-0"
                  (click)="openEmailVerificationConfirm()"
                  [title]="user.emailVerified ? 'Mark as unverified' : 'Mark as verified'"
                  [attr.aria-label]="user.emailVerified ? 'Mark email as unverified' : 'Mark email as verified'"
                >
                  <i class="bi" [class.bi-toggle-on]="user.emailVerified" [class.bi-toggle-off]="!user.emailVerified"></i>
                </button>
              </div>
            </div>

            <div class="py-2 border-bottom">
              <label class="small text-muted d-block mb-2">Two-Factor Authentication</label>
              <div>
                <span class="badge" [class.bg-success]="user.is2faEnabled" [class.bg-secondary]="!user.is2faEnabled">
                  <i class="bi" [class.bi-shield-check]="user.is2faEnabled" [class.bi-shield-x]="!user.is2faEnabled"></i>
                  {{ user.is2faEnabled ? 'Enabled' : 'Disabled' }}
                </span>
                <small *ngIf="user.is2faEnabled && user.twoFactorMethod" class="text-muted ms-2">
                  ({{ user.twoFactorMethod === '2fa-totp' ? 'Authenticator App' : 'SMS' }})
                </small>
              </div>
            </div>

            <div class="pt-2">
              <label class="small text-muted d-block mb-2">Account Status</label>
              <div>
                <span class="badge" [class.bg-success]="!isLocked" [class.bg-danger]="isLocked">
                  <i class="bi" [class.bi-unlock]="!isLocked" [class.bi-lock]="isLocked"></i>
                  {{ isLocked ? 'Locked' : 'Active' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-gear me-2"></i>
              Actions
            </h5>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <!-- Email Verification -->
              <button
                *ngIf="!user.emailVerified"
                type="button"
                class="btn btn-outline-primary btn-sm"
                (click)="resendVerificationEmail()"
              >
                <i class="bi bi-envelope me-2"></i>
                Resend Verification Email
              </button>

              <!-- Password Reset -->
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                (click)="openPasswordResetConfirm()"
              >
                <i class="bi bi-key me-2"></i>
                Send Password Reset
              </button>

              <!-- Disable 2FA -->
              <button
                *ngIf="user.is2faEnabled"
                type="button"
                class="btn btn-outline-warning btn-sm"
                (click)="disable2FA()"
              >
                <i class="bi bi-shield-x me-2"></i>
                Disable 2FA
              </button>

              <!-- Set Password Reset -->
               <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                (click)="openPasswordResetModal()"
               >
                <i class="bi me-2"
                  [class.bi-shield-lock]="!user.requiresPasswordReset"
                  [class.bi-shield-slash]="user.requiresPasswordReset"></i>
                {{ user.requiresPasswordReset ? 'Clear' : 'Require'}} Password Reset

               </button>

              <!-- Lock/Unlock Account -->
              <button
                type="button"
                class="btn btn-sm"
                [class.btn-outline-danger]="!isLocked"
                [class.btn-outline-success]="isLocked"
                (click)="openLockConfirm()"
              >
                <i class="bi me-2" [class.bi-lock]="!isLocked" [class.bi-unlock]="isLocked"></i>
                {{ isLocked ? 'Unlock Account' : 'Lock Account' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Invitations -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-envelope-paper me-2"></i>
              Invitations ({{ invitations.length }})
            </h5>
          </div>
          <div class="card-body">
            <!-- Loading -->
            <div *ngIf="loadingInvitations" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- No Invitations -->
            <div *ngIf="!loadingInvitations && invitations.length === 0" class="text-center text-muted py-3">
              <i class="bi bi-inbox" style="font-size: 1.5rem;"></i>
              <p class="mt-2 mb-0 small">No invitations found</p>
            </div>

            <!-- Invitation List -->
            <div *ngIf="!loadingInvitations && invitations.length > 0">
              <div
                *ngFor="let invitation of invitations"
                class="invitation-card p-3 border rounded mb-3"
              >
                <!-- Source and Status Badges -->
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge" [ngClass]="getSourceBadgeClass(invitation.source)">
                      {{ getSourceText(invitation.source) }}
                    </span>
                    <span class="badge" [ngClass]="getStatusBadgeClass(invitation.status)">
                      {{ invitation.status | titlecase }}
                    </span>
                  </div>
                  <button
                      type="button"
                      class="btn btn-link text-danger p-0"
                      title="Delete invitation"
                      aria-label="Delete invitation"
                      (click)="deleteInvitation(invitation)"
                      [disabled]="deletingInvitationId === invitation.id"
                      >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>

                <!-- Type / Role -->
                <div class="mb-1">
                  <label class="small text-muted">Type</label>
                  <div class="text-capitalize small fw-medium">{{ formatInvitationType(invitation.type, invitation) }}</div>
                </div>

                <!-- Organization Name (for org invitations) -->
                <div *ngIf="invitation.organizationName" class="mb-1">
                  <label class="small text-muted">Organization</label>
                  <div class="small fw-medium">{{ invitation.organizationName }}</div>
                </div>

                <!-- Email Status -->
                <div class="mb-1">
                  <label class="small text-muted">Email</label>
                  <div>
                    <span class="badge small" [ngClass]="getEmailStatusBadgeClass(invitation.emailStatus)">
                      {{ getEmailStatusText(invitation.emailStatus) }}
                    </span>
                  </div>
                </div>

                <!-- Dates -->
                <div class="mb-1">
                  <label class="small text-muted">Sent</label>
                  <div class="small">{{ formatDate(invitation.createdAt) }}</div>
                </div>

                <div class="mb-2">
                  <label class="small text-muted">Expires</label>
                  <div class="small">
                    <span [class.text-danger]="isInvitationExpired(invitation)">
                      {{ formatDate(invitation.expiresAt) }}
                    </span>
                    <span *ngIf="isInvitationExpired(invitation)" class="badge bg-danger ms-1 small">Expired</span>
                  </div>
                </div>

                <!-- Resend Count -->
                <div *ngIf="invitation.resendCount > 0" class="text-muted small mb-2">
                  Resent {{ invitation.resendCount }} of 5 times
                </div>

                <!-- Management Controls -->
                @if (invitation.emailStatus === 'never_sent') {

                  <button
                    type="button"
                    class="btn btn-outline-success btn-sm w-100"
                    [disabled]="sendingInvitationId === invitation.id"
                    (click)="sendInvitation(invitation)">
                   @if (sendingInvitationId === invitation.id) {
                    <span class="spinner-border spinner-border-sm me-1">
                      <span class="visually-hidden">Sending...</span>
                    </span>
                   } @else {
                    <i class="bi bi-send me-1"></i>
                   }
                   Send
                  </button>
                } @else {
                  <div *ngIf="invitation.status === 'pending' || invitation.status === 'expired'" class="d-flex gap-2 mt-2">
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm flex-fill"
                      [disabled]="resendingInvitationId === invitation.id || invitation.resendCount >= 5"
                      (click)="resendInvitation(invitation)"
                    >
                      <span *ngIf="resendingInvitationId === invitation.id" class="spinner-border spinner-border-sm me-1">
                        <span class="visually-hidden">Sending...</span>
                      </span>
                      <i *ngIf="resendingInvitationId !== invitation.id" class="bi bi-send me-1"></i>
                      Resend
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm flex-fill"
                      [disabled]="cancellingInvitationId === invitation.id"
                      (click)="cancelInvitation(invitation)"
                    >
                      <span *ngIf="cancellingInvitationId === invitation.id" class="spinner-border spinner-border-sm me-1">
                        <span class="visually-hidden">Cancelling...</span>
                      </span>
                      <i *ngIf="cancellingInvitationId !== invitation.id" class="bi bi-x-circle me-1"></i>
                      Cancel
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Roles & Permissions -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-diagram-3 me-2"></i>
              Roles & Permissions
            </h5>
          </div>
          <div class="card-body">
            <!-- Loading Claims -->
            <div *ngIf="loadingClaims" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- No Claims -->
            <div *ngIf="!loadingClaims && !userClaims" class="text-center text-muted py-3">
              <i class="bi bi-exclamation-circle" style="font-size: 1.5rem;"></i>
              <p class="mt-2 mb-0 small">Unable to load claims</p>
            </div>

            <!-- Claims Display -->
            <div *ngIf="!loadingClaims && userClaims">
              <!-- Global Roles -->
              <div class="mb-3">
                <label class="small text-muted d-block mb-2">Global Roles</label>
                <div *ngIf="userClaims.globalRoles.length === 0" class="text-muted small">
                  No global roles assigned
                </div>
                <div *ngIf="userClaims.globalRoles.length > 0">
                  <span
                    *ngFor="let role of userClaims.globalRoles"
                    class="badge me-1"
                    [class.bg-danger]="role === 'Super Admin'"
                    [class.bg-primary]="role === 'Admin'"
                  >
                    <i class="bi" [class.bi-shield-fill-exclamation]="role === 'Super Admin'" [class.bi-shield-fill]="role === 'Admin'"></i>
                    {{ role }}
                  </span>
                </div>
              </div>

              <!-- Admin Toggle (Super Admin only) -->
              <div *ngIf="currentUserIsSuperAdmin && !userClaims.isSuperAdmin" class="mb-3 pt-2 border-top">
                <label class="small text-muted d-block mb-2">Admin Access</label>
                <button
                  type="button"
                  class="btn btn-sm w-100"
                  [class.btn-outline-primary]="!userClaims.isAdmin"
                  [class.btn-outline-danger]="userClaims.isAdmin"
                  data-bs-toggle="modal"
                  data-bs-target="#adminRoleModal"
                >
                  <i class="bi me-2" [class.bi-shield-plus]="!userClaims.isAdmin" [class.bi-shield-minus]="userClaims.isAdmin"></i>
                  {{ userClaims.isAdmin ? 'Revoke Admin' : 'Grant Admin' }}
                </button>
              </div>

              <!-- Organization Memberships -->
              <div class="pt-2 border-top">
                <label class="small text-muted d-block mb-2">Organization Memberships</label>
                <div *ngIf="userClaims.memberships.length === 0" class="text-muted small">
                  No organization memberships
                </div>
                <div *ngFor="let membership of userClaims.memberships" class="membership-item mb-2 p-2 border rounded">
                  <div class="fw-medium small d-flex align-items-center justify-content-between">
                    <a [routerLink]="['/organizations', membership.organizationId]" class="text-decoration-none">
                      {{ membership.organizationName }}
                    </a>
                    @let orgBadge = getMembershipPlanBadge(membership.membershipPlan);
                    <span [class]="orgBadge.badge" title="This organization's own live Core/Pro tier.">
                      <i [class]="orgBadge.icon"></i>
                      {{ orgBadge.label }}
                    </span>
                  </div>
                  <div class="mt-1">
                    <span
                      *ngFor="let role of membership.roles"
                      class="badge bg-secondary me-1 small"
                    >
                      {{ role }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- coordinates lookup confirmation modal that shows the found coordinates plus a map  -->
@if (pendingCoordinates) {
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg w-100" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Confirm Coordinates</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" [disabled]="savingCoordinates" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <p class="text-body-secondary small mb-3">
            Found from: {{ user?.address }}, {{ user?.city }}, {{ user?.state }} {{ user?.zipCode }}
          </p>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              @if (user?.latitude != null) {
                <div>{{ user?.latitude | number:'1.5-5' }}, {{ user?.longitude | number:'1.5-5' }}</div>
              } @else {
                <div class="text-body-secondary fst-italic">Not set</div>
              }
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <div class="fw-semibold">
                {{ pendingCoordinates.lat | number:'1.5-5' }}, {{ pendingCoordinates.lng | number:'1.5-5' }}
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingCoordinates"></map-marker>
            </google-map>
          </div>

          @if (saveError) {
            <div class="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
              <i class="bi bi-x-circle-fill me-2 mt-1"></i>
              <div>
                <div class="fw-semibold">Save failed</div>
                <div class="small">{{ saveError }}</div>
              </div>
            </div>
          }
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()" [disabled]="savingCoordinates">
            Decline
          </button>
          <button type="button" class="btn btn-primary" (click)="confirmedCoordinates()" [disabled]="savingCoordinates">
            @if (savingCoordinates) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            }
            {{ savingCoordinates ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- manual coordinate entry for failed lookup call  -->
@if (pendingManualCoordinates) {
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Manual Coordinate Entry</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" [disabled]="savingCoordinates" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div class="alert alert-warning d-flex align-items-start mb-3" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
            <div>
              <div class="fw-semibold">Lookup failed</div>
              <div class="small">{{ lookupError }} Enter the latitude and longitude manually below.</div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              @if (user?.latitude != null) {
                <div>{{ user?.latitude | number:'1.5-5' }}, {{ user?.longitude | number:'1.5-5' }}</div>
              } @else {
                <div class="text-body-secondary fst-italic">Not set</div>
              }
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <div class="row g-2">
                <div class="col">
                  <label class="form-label small text-body-secondary mb-1">Lat</label>
                  <input
                    type="number"
                    class="form-control"
                    placeholder="Latitude"
                    min="-90" max="90" step="any" required
                    #latInput="ngModel"
                    [ngModel]="pendingManualCoordinates.lat"
                    (ngModelChange)="updateManualCoordinate('lat', $event)"
                    [class.is-invalid]="latInput.invalid && latInput.touched"
                  />
                  @if (latInput.invalid && latInput.touched) {
                    <div class="invalid-feedback">Please enter a valid latitude</div>
                  }
                </div>
                <div class="col">
                  <label class="form-label small text-body-secondary mb-1">Long</label>
                  <input
                    type="number"
                    class="form-control"
                    placeholder="Longitude"
                    min="-180" max="180" step="any" required
                    #lngInput="ngModel"
                    [ngModel]="pendingManualCoordinates.lng"
                    (ngModelChange)="updateManualCoordinate('lng', $event)"
                    [class.is-invalid]="lngInput.invalid && lngInput.touched"
                  />
                  @if (lngInput.invalid && lngInput.touched) {
                    <div class="invalid-feedback">Please enter a valid longitude</div>
                  }
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingManualCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingManualCoordinates"></map-marker>
            </google-map>
          </div>

          @if (saveError) {
            <div class="alert alert-danger d-flex align-items-start mt-3 mb-0" role="alert">
              <i class="bi bi-x-circle-fill me-2 mt-1"></i>
              <div>
                <div class="fw-semibold">Save failed</div>
                <div class="small">{{ saveError }}</div>
              </div>
            </div>
          }
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()" [disabled]="savingCoordinates">
            Decline
          </button>
          <button
            type="button"
            class="btn btn-primary"
            (click)="confirmedCoordinates()"
            [disabled]="savingCoordinates || latInput.invalid || lngInput.invalid">
            @if (savingCoordinates) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            }
            {{ savingCoordinates ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}
